import React, { useState } from 'react';
import axios from 'axios';
import { FileText, Download, Image as ImageIcon, Mail, Phone, Copy, Check, Crop } from 'lucide-react';
import Reveal from './Reveal';
import { PRESS, PRESS_IMAGES } from '../data/press';
import { generatePressReleasePDF } from '../utils/generatePressRelease';
import { generateImagePack } from '../utils/generatePressPack';
import { generateCropsPack, CROP_SIZES } from '../utils/generateCropsPack';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const track = (kind) => axios.post(`${API}/press/track`, { kind }).catch(() => {});

const PressKit = () => {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [cropsBusy, setCropsBusy] = useState(false);
  const [cropsProgress, setCropsProgress] = useState({ done: 0, total: 0, current: '' });
  const [copied, setCopied] = useState(null);

  const copyBlurb = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch (e) {
      console.error(e);
      alert('Copy failed. Please select and copy manually.');
    }
  };

  const wordCount = (t) => t.trim().split(/\s+/).length;

  const onPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await generatePressReleasePDF();
      track('press_release');
    } catch (e) {
      console.error(e);
      alert('Sorry, the press release failed to generate. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const onZip = async () => {
    if (zipBusy) return;
    setZipBusy(true);
    try {
      await generateImagePack();
      track('image_pack');
    } catch (e) {
      console.error(e);
      alert('Sorry, the image pack failed to build. Please try again.');
    } finally {
      setZipBusy(false);
    }
  };

  const onCrops = async () => {
    if (cropsBusy) return;
    setCropsBusy(true);
    setCropsProgress({ done: 0, total: 0, current: '' });
    try {
      await generateCropsPack((p) => setCropsProgress(p));
      track('crop_pack');
    } catch (e) {
      console.error(e);
      alert('Sorry, the crop pack failed to build. Please try again.');
    } finally {
      setCropsBusy(false);
    }
  };

  return (
    <section
      id="press"
      className="relative py-24 md:py-32 px-6 md:px-10 border-t border-[#3a2818]/40"
    >
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <p className="text-[10px] tracking-[0.5em] text-[#c8935a] font-fell-sc text-center">
            FOR IMMEDIATE RELEASE
          </p>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="mt-6 font-display text-5xl md:text-6xl text-[#e8dcc0] lowercase text-center">
            press
          </h2>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 divider-ornament max-w-md mx-auto">
            <span className="text-[9px] tracking-[0.4em] font-fell-sc">PRESS KIT</span>
          </div>
        </Reveal>

        <Reveal delay={300}>
          <p className="mt-8 font-fell italic text-lg md:text-xl text-[#d9c8a3] text-center max-w-2xl mx-auto leading-relaxed">
            Downloadable press release and hi-res image pack for editorial
            coverage of ASH — The Witch Trials of Drudenhaus.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <Reveal delay={350}>
            <button
              onClick={onPdf}
              disabled={pdfBusy}
              data-testid="press-download-pdf-button"
              className="group w-full text-left relative overflow-hidden border border-[#c8935a]/40 hover:border-[#c8935a] bg-[#0f0906]/60 hover:bg-[#140d08] transition-all duration-500 p-7 md:p-8"
            >
              <div className="flex items-start gap-5">
                <div className="shrink-0 w-11 h-11 rounded-full border border-[#c8935a]/60 flex items-center justify-center text-[#c8935a] group-hover:bg-[#c8935a]/10 transition-colors">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase">
                    Document · PDF
                  </p>
                  <h3 className="mt-2 font-display text-xl md:text-2xl text-[#e8dcc0]">
                    Press Release
                  </h3>
                  <p className="mt-2 font-fell text-sm text-[#a89880] leading-relaxed">
                    Multi-page release with headline, dates, quotes, key facts,
                    photography and press contact.
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.35em] font-fell-sc uppercase text-[#e8dcc0] group-hover:text-[#d9a55e] transition-colors">
                    <Download className="w-3.5 h-3.5" />
                    {pdfBusy ? 'Preparing…' : 'Download PDF'}
                  </div>
                </div>
              </div>
              <span className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{
                background:
                  'radial-gradient(circle at 90% 10%, rgba(220,150,70,0.15) 0%, transparent 60%)'
              }} />
            </button>
          </Reveal>

          <Reveal delay={450}>
            <button
              onClick={onZip}
              disabled={zipBusy}
              data-testid="press-download-zip-button"
              className="group w-full text-left relative overflow-hidden border border-[#c8935a]/40 hover:border-[#c8935a] bg-[#0f0906]/60 hover:bg-[#140d08] transition-all duration-500 p-7 md:p-8"
            >
              <div className="flex items-start gap-5">
                <div className="shrink-0 w-11 h-11 rounded-full border border-[#c8935a]/60 flex items-center justify-center text-[#c8935a] group-hover:bg-[#c8935a]/10 transition-colors">
                  <ImageIcon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase">
                    Image pack · ZIP
                  </p>
                  <h3 className="mt-2 font-display text-xl md:text-2xl text-[#e8dcc0]">
                    Photography
                  </h3>
                  <p className="mt-2 font-fell text-sm text-[#a89880] leading-relaxed">
                    {PRESS_IMAGES.length} hi-res images — production stills and the
                    official key art poster — plus captions, credits and a README for editors.
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.35em] font-fell-sc uppercase text-[#e8dcc0] group-hover:text-[#d9a55e] transition-colors">
                    <Download className="w-3.5 h-3.5" />
                    {zipBusy ? 'Bundling…' : 'Download ZIP'}
                  </div>
                </div>
              </div>
            </button>
          </Reveal>
        </div>

        {/* Crop pack for ticketing / social sizes */}
        <Reveal delay={500}>
          <button
            onClick={onCrops}
            disabled={cropsBusy}
            data-testid="press-download-crops-button"
            className="group mt-6 w-full text-left relative overflow-hidden border border-[#c8935a]/40 hover:border-[#c8935a] bg-[#0f0906]/60 hover:bg-[#140d08] transition-all duration-500 p-7 md:p-8 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <div className="flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 rounded-full border border-[#c8935a]/60 flex items-center justify-center text-[#c8935a] group-hover:bg-[#c8935a]/10 transition-colors">
                <Crop className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase">
                  Crop pack · ZIP · {CROP_SIZES.length} sizes
                </p>
                <h3 className="mt-2 font-display text-xl md:text-2xl text-[#e8dcc0]">
                  Every crop for every ticketing site
                </h3>
                <p className="mt-2 font-fell text-sm text-[#a89880] leading-relaxed">
                  Every production still auto-cropped into all {CROP_SIZES.length} sizes
                  required by ticketing platforms, listings and social channels —
                  squares, portraits, landscapes and super-wide banners from 300×200
                  up to 1920×1080. Smart-cropped around the Inquisitor and the candles.
                </p>

                {cropsBusy ? (
                  <div className="mt-5">
                    <div className="h-[3px] w-full bg-[#3a2818] overflow-hidden">
                      <div
                        className="h-full bg-[#c8935a] transition-all duration-300"
                        style={{
                          width: `${
                            cropsProgress.total
                              ? Math.round((cropsProgress.done / cropsProgress.total) * 100)
                              : 4
                          }%`
                        }}
                      />
                    </div>
                    <p className="mt-3 font-fell italic text-xs text-[#8b7355]">
                      {cropsProgress.total
                        ? `${cropsProgress.done} of ${cropsProgress.total} — ${cropsProgress.current}`
                        : 'Loading images…'}
                    </p>
                  </div>
                ) : (
                  <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.35em] font-fell-sc uppercase text-[#e8dcc0] group-hover:text-[#d9a55e] transition-colors">
                    <Download className="w-3.5 h-3.5" />
                    Build & download ZIP
                  </div>
                )}
              </div>
            </div>
          </button>
        </Reveal>

        {/* Blurbs — for ticketing / listings sites */}
        <Reveal delay={520}>
          <div className="mt-14">
            <div className="text-center">
              <p className="text-[10px] tracking-[0.5em] font-fell-sc text-[#c8935a] uppercase">
                For listings & ticketing sites
              </p>
              <h3 className="mt-4 font-display text-3xl md:text-4xl text-[#e8dcc0] lowercase">
                ready-made blurbs
              </h3>
              <p className="mt-3 font-fell italic text-sm text-[#8b7355]">
                Copy and paste — a short listing line and a longer sales-page paragraph.
              </p>
            </div>

            <div className="mt-10 grid md:grid-cols-2 gap-6">
              {/* 50 word */}
              <div className="relative border border-[#3a2818]/70 bg-[#0d0805]/60 p-6 md:p-7">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full border border-[#c8935a]/60 flex items-center justify-center text-[10px] font-fell-sc tracking-widest text-[#c8935a]">
                      50
                    </span>
                    <div>
                      <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase">
                        Short blurb
                      </p>
                      <p className="text-[11px] font-fell italic text-[#8b7355]">
                        {wordCount(PRESS.blurbs.fifty)} words · ideal for TodayTix, DesignMyNight listings
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => copyBlurb('fifty', PRESS.blurbs.fifty)}
                    aria-label="Copy short blurb"
                    className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] font-fell-sc uppercase text-[#c8935a] hover:text-[#f4e8ce] transition-colors border border-[#c8935a]/40 hover:border-[#c8935a] px-3 py-1.5"
                  >
                    {copied === 'fifty' ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="mt-5 font-fell text-sm md:text-[15px] text-[#d9c8a3] leading-relaxed">
                  {PRESS.blurbs.fifty}
                </p>
              </div>

              {/* 200 word */}
              <div className="relative border border-[#3a2818]/70 bg-[#0d0805]/60 p-6 md:p-7">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full border border-[#c8935a]/60 flex items-center justify-center text-[10px] font-fell-sc tracking-widest text-[#c8935a]">
                      200
                    </span>
                    <div>
                      <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase">
                        Long blurb
                      </p>
                      <p className="text-[11px] font-fell italic text-[#8b7355]">
                        {wordCount(PRESS.blurbs.twoHundred)} words · sales-page description
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => copyBlurb('twoHundred', PRESS.blurbs.twoHundred)}
                    aria-label="Copy long blurb"
                    className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] font-fell-sc uppercase text-[#c8935a] hover:text-[#f4e8ce] transition-colors border border-[#c8935a]/40 hover:border-[#c8935a] px-3 py-1.5"
                  >
                    {copied === 'twoHundred' ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <div className="mt-5 max-h-64 overflow-y-auto pr-2 font-fell text-sm md:text-[14.5px] text-[#d9c8a3] leading-relaxed whitespace-pre-line">
                  {PRESS.blurbs.twoHundred}
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Press contact card */}
        <Reveal delay={550}>
          <div className="mt-14 border border-[#3a2818]/70 bg-[#0d0805]/60 p-7 md:p-9">
            <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase">
              Press enquiries
            </p>
            <p className="mt-3 font-display text-lg md:text-xl text-[#e8dcc0]">
              {PRESS.press.name}
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-4 sm:gap-8 items-start sm:items-center">
              <a
                href={`mailto:${PRESS.press.email}?subject=Press%20enquiry%20—%20ASH%20The%20Witch%20Trials%20of%20Drudenhaus`}
                className="inline-flex items-center gap-2 text-[#e8dcc0] hover:text-[#d9a55e] transition-colors text-sm font-fell"
              >
                <Mail className="w-4 h-4 text-[#c8935a]" />
                {PRESS.press.email}
              </a>
              <a
                href={`tel:${PRESS.press.phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-2 text-[#e8dcc0] hover:text-[#d9a55e] transition-colors text-sm font-fell"
              >
                <Phone className="w-4 h-4 text-[#c8935a]" />
                {PRESS.press.phone}
              </a>
            </div>
            <p className="mt-4 font-fell italic text-xs text-[#8b7355]">
              Written, directed & performed by Bertie Watkins
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default PressKit;
